import { Injectable, Injector } from "@angular/core";
import { doc, docData, Firestore } from "@angular/fire/firestore";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Data } from "@angular/router";
import { AuthService } from "@core/auth.service";
import { firstValueFrom, Observable, of, take } from "rxjs";
import { map, switchMap } from "rxjs/operators";
import { RequestBodyService } from "src/app/services/request-body.service";
import { BaseService } from "../app.base.service";
import { CommonVariable } from "../app.constants";
import { MyToastbarComponent } from "../components/my-toastbar/my-toastbar.component";
import { RequestProfileService } from "./RequestBodyService/RequestProfile/request-profile.service";
import { RequestUserService } from "./RequestBodyService/RequestUser/request-user.service";
import { CommonService } from "./common/common.service";
import { FirestoreDataService } from "./firestore-data.service";
import { UserManagementService } from "./user-management.service";


@Injectable({
  providedIn: "root",
})
export class NotificationsService extends BaseService {
  constructor(
    private firestore: Firestore,
    public requestBodyService: RequestBodyService,
    private injector: Injector,
    private RequestProfileService: RequestProfileService,
    private RequestUserService: RequestUserService,
    private firestoreDataService: FirestoreDataService,
    private userManagementService: UserManagementService,
    private snackBar: MatSnackBar,
    private commonService: CommonService
  ) {
    super(injector);
  }

  sendLanguageRequest() {
    const authService = this.injector.get(AuthService);

    try {
      return this.RequestUserService.updateUserpreferredLanguage().pipe(
        switchMap((requestData) => {
          if (!requestData) {
            return of(null);
          }
          return authService.v2CloudFunctionResponse(this.callableFunction(requestData));
        })
      )
        .subscribe({
          next: (res) => console.log("Language update success:", res),
          error: (err) => console.error("Language update failed:", err),
        });
    } catch (error) {
      console.error("Error in sendLanguageRequest:", error);
      throw new Error(`Error in sendLanguageRequest ${error}`);
    }
  }
  processCardPayment(customerOnesignalId) {
    try {
      const authService = this.injector.get(AuthService);
      return authService
        ?.v2CloudFunctionResponse(
          this.callableFunction(this.RequestProfileService.OnesignalNotificationID(customerOnesignalId))
        )
        .subscribe(
          () => { },
          (err) => {
            return err;
          }
        );
    } catch (error) {
      throw new Error(error);
    }
  }

  public getConstantsMsg(msgCode) {
    return new Promise<any>((resolve, reject) => {
      try {
        // let toastMsgData;
        this.firestoreDataService
          .getAngularFireStore(`/Global_lookup/toast_message/list/${msgCode}`, "get")
          ?.subscribe((msg: Data) => {
            // This variable contains the massage data
            const msgExists = msg?.exists;
            const msgData = msg?.data();
            //  This Condition checks if the message data null and undefined
            if (msgExists != null && msgExists != undefined && msgData != null && msgData != undefined) {
              const msgData = JSON.parse(JSON.stringify(msg.data()));
              let toastMsg: string;
              if (msgData?.message?.[this.userManagementService.selectedlanguage$?.value]) {
                toastMsg = msgData?.message?.[this.userManagementService.selectedlanguage$?.value];
              } else if (msgData?.message?.[CommonVariable?.preferredLanguage]) {
                toastMsg = msgData?.message?.[CommonVariable?.preferredLanguage];
              }
              const msgStyle = msgData?.style;
              const toastMsgData = {
                message: toastMsg ? toastMsg : {},
                style: msgStyle,
              };
              resolve(toastMsgData);
            } else {
              resolve(null);
            }
          });
      } catch (err) {
        reject(err);
      }
    });
  }

  /**
   * It takes a translated message and a default message, and then it shows a toast message with the
   * translated message if it exists, otherwise it shows the default message.
   * </code>
   * @param translatedMsg - The translated message object
   * @param defaultMsg - This is the default message that will be shown if the translation is not
   * found.
   */
  public showToastMessage(translatedMsg, defaultMsg): void {
    const message = translatedMsg?.message ? translatedMsg?.message : defaultMsg?.message;
    const icon = translatedMsg?.style?.icon ? translatedMsg?.style?.icon : defaultMsg?.style?.icon;
    const snackType = translatedMsg?.style?.type ? translatedMsg?.style?.type : defaultMsg?.style?.type;
    const duration = translatedMsg?.style?.duration ? translatedMsg?.style?.duration : 2000;
    this.snackBar.openFromComponent(MyToastbarComponent, {
      duration: duration,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: "toast-for-syndicate",
      data: { message: message, duration: duration, icon: icon, state: snackType },
    });
  }

  /**
   * It opens a snackbar with a custom component, and passes data to that component.
   * </code>
   * @param message - The message you want to display in the toast.
   * @param snackType - This is the type of snackbar you want to show. It can be success, error,
   * warning, info.
   * @param description - This is the message that will be displayed in the toast.
   */
  showToast(message, snackType, description) {
    this.snackBar.openFromComponent(MyToastbarComponent, {
      duration: 5000,
      horizontalPosition: "center",
      verticalPosition: "top",
      panelClass: "toast-for-syndicate",
      data: { message: message, state: snackType, description: description },
    });
  }

  //Set Notification set as seen
  setNotificationFlag() {
    this.commonService.getUid("NotificationsService").pipe(take(1)).subscribe({
      next: (userId) => {
        if (!userId) return; // Ensure userId is valid before proceeding

        const path_id = "yzF1Ss5eiMqnh91zQMtv";
        const doc_data = { notification_seen: true };
        const method = "update";

        this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
          path_id,
          doc_data,
          method,
        }).subscribe({
          error: (error) => {
            console.error("Error updating notification flag:", error);
          }
        });
      },
      error: (err) => {
        console.error("Error fetching userId in setNotificationFlag:", err);
      },
    });
  }

  // This code hides notifications for the direct message tab. This is done by calling the
  // readNotification function. This function updates the notification status to read for the
  // current user, so that the notifications are no longer displayed.
  hideDmNotifications() {
    this.commonService.getUid("NotificationsService").pipe(take(1)).subscribe({
      next: (userId) => {
        if (!userId) return;

        this.firestoreDataService
          .getV2CloudFunction("readnotificationv2", { userId })
          ?.subscribe();
      },
      error: (err) => {
        console.error("Error fetching userId in hideDmNotifications:", err);
      },
    });
  }


  // This function is used to send a welcome email to a user, and is called when a user signs up for the application. It is a HTTPS callable function, and is called from the Angular frontend.
  sendWelcomeEmail() {
    this.commonService.getUid("NotificationsService").pipe(take(1)).subscribe({
      next: (userId) => {
        if (!userId) return;

        const data = { userId };
        this.firestoreDataService
          .getV2CloudFunction("sendemailv2", data)
          ?.subscribe();
      },
      error: (err) => {
        console.error("Error fetching userId in sendWelcomeEmail:", err);
      },
    });
  }

  claimInlineVoucher(notificationId: string): Promise<Data> {
    return firstValueFrom(
      this.commonService.getUid("claimInlineVoucher").pipe(
        switchMap(userId =>
          this.firestoreDataService.getV2CloudFunction("claiminlinevoucher", {
            userId,
            notificationId,
          })
        )
      )
    );
  }


  async onClaiminlinevoucher(notification: Data){
    const path = notification.notification_id?.path;
    const notificationId = notification.notification_id?.id;
    try {
      const result = await this.claimInlineVoucher(notificationId);
      if (result?.success) {
        this.updateClaiminlineVoucherDismiss(path);
        this.showToastMsg(true);
      } else {
        this.showToastMsg(false);
      }
      return result;
    } catch (error) {
      console.error('[InlineVoucher] ERROR while claiming voucher:', error);
    }
  }


  updateClaiminlineVoucherDismiss(path: string): void {
    if (!path) return;
    const path_id = "pGxBYtRO4mdgNkboYdSy";
    const doc_data = { dismissed: true, viewed: true };
    const dv = { docId: path?.substring(path.lastIndexOf("/") + 1) || null };
    const method = "update";

    this.firestoreDataService.getV2CloudFunction("updatefirestoredoc", {
      path_id,
      doc_data,
      dv,
      method,
    }).subscribe({
      error: (error) => {
        console.error("Error dismissing:", error);
      }
    });
  }

  showToastMsg(success: boolean): void {
    const icon = success ? "tick circle success.png" : "tick circle error.svg";
    const type = success ? "Success" : "Error";
    const msgId = success ? "1161" : "1162";
    this.getConstantsMsg(msgId).then(
      (translatedMsg) => {
        const toastData = {
          message: {
            line_1: translatedMsg?.line_1 || "",
            line_2: translatedMsg?.line_2 || "",
          },
          style: { type, icon },
        };
        this.showToastMessage(translatedMsg, toastData);
      }
    );
  }

  // Countdown monitor

  startCountdownMonitor(claimExpirySeconds: number, inlineId: string): Observable<number | null> {
    return this.getInlineNotificationData(inlineId).pipe(
      map((data: Data) => {
        const lastClaimStatus = data?.claim_status;
        if (lastClaimStatus !== "claim") return null;

        const expiryTime = claimExpirySeconds * 1000;
        const currentTime = Date.now();
        const secondsLeft = Math.floor((expiryTime - currentTime) / 1000);

        return secondsLeft > 0 ? secondsLeft : 0;
      })
    );
  }


  getInlineNotificationData(inlineId: string): Observable<Data> {
    return this.commonService.getUid("").pipe(
      switchMap((uid) => {
        const docRef = doc(this.firestore, `users/${uid}/inline_notification/${inlineId}`);
        return docData(docRef) as Observable<Data>;
      })
    );
  }
}
